import { useMemo } from 'react'
import PropTypes from 'prop-types'

import { useModals, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'

import BrandSettingsModal from '@modals/BrandSettingsModal'
import CreateContractModal from '@modals/CreateContractModal'
import CreateOrEditAttachmentModal from '@modals/CreateOrEditAttachmentModal'
import CreateOrEditBrandColourModal from '@modals/CreateOrEditBrandColourModal'
import CreateOrEditBrandThemeModal from '@modals/CreateOrEditBrandThemeModal'
import ManageActivityModal from '@modals/ManageActivityModal'
import ManageBrandPublicLinkAttachmentsModal from '@modals/ManageBrandPublicLinkAttachmentsModal'
import ManageBrandPublicLinkSettingsModal from '@modals/ManageBrandPublicLinkSettingsModal'
import ManageBrandSSOModal from '@modals/ManageBrandSSOModal'
import ManageBrandSystemColoursModal from '@modals/ManageBrandSystemColoursModal'
import ManageEntityQuickLinksModal from '@modals/ManageEntityQuickLinksModal'
import UploadBrandImageModal from '@modals/UploadBrandImageModal'
import UploadEntityAssetsModal from '@modals/UploadEntityAssetsModal'

import PageContent from './components/PageContent'

const callbacks = (component, setState) => {
  const componentCallbacks = {
    BrandSettingsModal: {
      closeModal: () => setState({ showBrandSettingsModal: false }),
      updateBrand: payload => handleCallbackAction(payload),
    },
    CreateContractModal: {
      createContract: payload => handleCallbackAction(payload),
      closeModal: () => setState({ showCreateContractModal: false }),
    },
    CreateOrEditAttachmentModal: {
      closeModal: () => setState({ showCreateOrEditAttachmentModal: false }),
      deleteAttachment: payload => handleCallbackAction(payload),
      updateAttachment: payload => handleCallbackAction(payload),
    },
    CreateOrEditBrandColourModal: {
      closeModal: () => setState({ showCreateOrEditBrandColourModal: false }),
      createBrandColour: payload => handleCallbackAction(payload),
      deleteBrandColour: payload => handleCallbackAction(payload),
      updateBrandColour: payload => handleCallbackAction(payload),
    },
    CreateOrEditBrandThemeModal: {
      closeModal: () => setState({ showCreateOrEditBrandThemeModal: false }),
      createBrandTheme: payload => handleCallbackAction(payload),
      updateBrandTheme: payload => handleCallbackAction(payload),
    },
    ManageActivityModal: {
      closeModal: () => setState({ showManageActivityModal: false }),
    },
    ManageBrandPublicLinkAttachmentsModal: {
      closeModal: () => setState({ showManageBrandPublicLinkAttachmentsModal: false }),
    },
    ManageBrandPublicLinkSettingsModal: {
      closeModal: () => setState({ showManageBrandPublicLinkSettingsModal: false }),
    },
    ManageBrandSSOModal: {
      closeModal: () => setState({ showManageBrandSSOModal: false }),
      updateBrand: payload => handleCallbackAction(payload),
    },
    ManageBrandSystemColoursModal: {
      closeModal: () => setState({ showManageBrandSystemColoursModal: false }),
      updateSystemBrandColours: payload => handleCallbackAction(payload),
    },
    ManageEntityQuickLinksModal: {
      closeModal: () => setState({ showManageEntityQuickLinksModal: false }),
      updateEntity: payload => handleCallbackAction(payload),
    },
    UploadBrandImageModal: {
      closeModal: () => setState({ showUploadBrandImageModal: false }),
      updateBrand: payload => handleCallbackAction(payload),
    },
    UploadEntityAssetsModal: {
      closeModal: () => setState({ showUploadEntityAssetsModal: false }),
      deleteAttachment: payload => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  showBrandSettingsModal: false,
  showCreateContractModal: false,
  showCreateOrEditAttachmentModal: false,
  showCreateOrEditBrandColourModal: false,
  showCreateOrEditBrandThemeModal: false,
  showManageActivityModal: false,
  showManageBrandPublicLinkAttachmentsModal: false,
  showManageBrandPublicLinkSettingsModal: false,
  showManageBrandSSOModal: false,
  showManageBrandSystemColoursModal: false,
  showManageEntityQuickLinksModal: false,
  showUploadBrandImageModal: false,
  showUploadEntityAssetsModal: false,
}

const Details = (props) => {
  const { brandId } = props

  const [state, setState] = useSetState(defaultState)
  const {
    showBrandSettingsModal,
    showCreateContractModal,
    showCreateOrEditAttachmentModal,
    showCreateOrEditBrandColourModal,
    showCreateOrEditBrandThemeModal,
    showManageActivityModal,
    showManageBrandPublicLinkAttachmentsModal,
    showManageBrandPublicLinkSettingsModal,
    showManageBrandSSOModal,
    showManageBrandSystemColoursModal,
    showManageEntityQuickLinksModal,
    showUploadBrandImageModal,
    showUploadEntityAssetsModal,
  } = state

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      deleteAttachment: payload => handleCallbackAction(payload),
      deleteContract: payload => handleCallbackAction(payload),
      exportBrand: payload => handleCallbackAction(payload),
      showBrandSettingsModal: (payload) => {
        setModalData('BrandSettingsModal', payload)
        setState({ showBrandSettingsModal: true })
      },

      showCreateContractModal: (payload) => {
        setModalData('CreateContractModal', payload)
        setState({ showCreateContractModal: true })
      },
      showCreateOrEditAttachmentModal: (payload) => {
        setModalData('CreateOrEditAttachmentModal', payload)
        setState({ showCreateOrEditAttachmentModal: true })
      },
      showCreateOrEditBrandColourModal: (payload) => {
        setModalData('CreateOrEditBrandColourModal', payload)
        setState({ showCreateOrEditBrandColourModal: true })
      },
      showCreateOrEditBrandThemeModal: (payload) => {
        setModalData('CreateOrEditBrandThemeModal', payload)
        setState({ showCreateOrEditBrandThemeModal: true })
      },
      showManageActivityModal: (payload) => {
        setModalData('ManageActivityModal', payload)
        setState({ showManageActivityModal: true })
      },
      showManageBrandPublicLinkAttachmentsModal: (payload) => {
        setModalData('ManageBrandPublicLinkAttachmentsModal', payload)
        setState({ showManageBrandPublicLinkAttachmentsModal: true })
      },
      showManageBrandPublicLinkSettingsModal: (payload) => {
        setModalData('ManageBrandPublicLinkSettingsModal', payload)
        setState({ showManageBrandPublicLinkSettingsModal: true })
      },
      showManageBrandSSOModal: (payload) => {
        setModalData('ManageBrandSSOModal', payload)
        setState({ showManageBrandSSOModal: true })
      },
      showManageBrandSystemColoursModal: (payload) => {
        setModalData('ManageBrandSystemColoursModal', payload)
        setState({ showManageBrandSystemColoursModal: true })
      },
      showManageEntityQuickLinksModal: (payload) => {
        setModalData('ManageEntityQuickLinksModal', payload)
        setState({ showManageEntityQuickLinksModal: true })
      },
      showUploadBrandImageModal: (payload) => {
        setModalData('UploadBrandImageModal', payload)
        setState({ showUploadBrandImageModal: true })
      },
      showUploadEntityAssetsModal: (payload) => {
        setModalData('UploadEntityAssetsModal', payload)
        setState({ showUploadEntityAssetsModal: true })
      },
      updateBrand: payload => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent brandId={brandId} />

        <CreateOrEditAttachmentModal
          callbacks={callbacks('CreateOrEditAttachmentModal', setState)}
          showModal={showCreateOrEditAttachmentModal}
        />

        <BrandSettingsModal
          callbacks={callbacks('BrandSettingsModal', setState)}
          showModal={showBrandSettingsModal}
        />

        <CreateContractModal
          callbacks={callbacks('CreateContractModal', setState)}
          showModal={showCreateContractModal}
        />

        <CreateOrEditBrandColourModal
          callbacks={callbacks('CreateOrEditBrandColourModal', setState)}
          showModal={showCreateOrEditBrandColourModal}
        />

        <CreateOrEditBrandThemeModal
          callbacks={callbacks('CreateOrEditBrandThemeModal', setState)}
          showModal={showCreateOrEditBrandThemeModal}
        />

        <ManageActivityModal
          callbacks={callbacks('ManageActivityModal', setState)}
          showModal={showManageActivityModal}
        />

        <ManageBrandPublicLinkAttachmentsModal
          callbacks={callbacks('ManageBrandPublicLinkAttachmentsModal', setState)}
          showModal={showManageBrandPublicLinkAttachmentsModal}
        />

        <ManageBrandPublicLinkSettingsModal
          callbacks={callbacks('ManageBrandPublicLinkSettingsModal', setState)}
          showModal={showManageBrandPublicLinkSettingsModal}
        />

        <ManageBrandSSOModal
          callbacks={callbacks('ManageBrandSSOModal', setState)}
          showModal={showManageBrandSSOModal}
        />

        <ManageBrandSystemColoursModal
          brandId={brandId}
          callbacks={callbacks('ManageBrandSystemColoursModal', setState)}
          showModal={showManageBrandSystemColoursModal}
        />

        <ManageEntityQuickLinksModal
          callbacks={callbacks('ManageEntityQuickLinksModal', setState)}
          showModal={showManageEntityQuickLinksModal}
        />

        <UploadBrandImageModal
          callbacks={callbacks('UploadBrandImageModal', setState)}
          showModal={showUploadBrandImageModal}
        />

        <UploadEntityAssetsModal
          callbacks={callbacks('UploadEntityAssetsModal', setState)}
          showModal={showUploadEntityAssetsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

Details.propTypes = {
  brandId: PropTypes.number.isRequired,
}

export default Details
